/** Services */
import { useState } from 'react'

import { Footer } from 'components'
import { BodyBackgroundWhite } from 'components/body-background-white'
import * as Sections from 'components/remote-work-guides'
/** Components */
import { Seo } from 'components/Seo'
import { GetServerSidePropsContext } from 'next'
import { useRouter } from 'next/router'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { getCommonServerSideProps } from 'services'
import { RemoteWorkGuidesClient as contentful } from 'services/contentful/clients'
import { RemoteGuides } from 'types/contentful'

import css from './Post.module.css'
/** Styles */
import adaptive from 'styles/adaptive.module.css'

type PostPageProps = {
    blogPost: RemoteGuides.Post
    postUrl: string
}

export default function PostPage({ blogPost, postUrl }: PostPageProps) {
    const [tableOfContentIsVisible, setTableOfContentVisible] = useState(false)
    const featuredImage = blogPost.coverImage.url
    const { title, description, backgroundColor } = blogPost
    const seoImageUrl = blogPost.seo?.image?.url
    const { asPath } = useRouter()

    return (
        <>
            <BodyBackgroundWhite />
            <Seo
                path={asPath}
                title={blogPost.seo?.title}
                generateAlternateUrls={false}
                description={blogPost.seo?.description}
                images={seoImageUrl ? [{ url: seoImageUrl }] : []}
                otherLanguages={[]}
            />

            <div className={css.postPage}>
                <div
                    className={css.postPageHeader}
                    style={{ backgroundColor: backgroundColor || 'white' }}
                >
                    <img className={css.featuredImage} src={featuredImage} />

                    <div className={adaptive.container}>
                        <div className={css.introContainer}>
                            <h1>{title}</h1>
                            <p>{description}</p>
                        </div>
                    </div>

                    <Sections.FixedHeader title={title} adaptive={adaptive} postUrl={asPath} />
                </div>

                {/* Id here for the purpose of anchoring */}
                <div
                    id="introduction"
                    className={`${css.tableOfContentContainer} ${adaptive.container}`}
                >
                    <Sections.TableOfContent
                        modalVisible={tableOfContentIsVisible}
                        collapseTableOfContent={() => setTableOfContentVisible(false)}
                    />

                    <Sections.ArticleContent richText={blogPost.content} />
                </div>

                <div id="subscribe" className={css.subscribeContainer}>
                    <h2>Want up-to-date advice on running a remote team?</h2>
                    <Sections.SubscribeBox />
                </div>

                <Sections.FixedFooter
                    title={title}
                    text={description}
                    url={postUrl}
                    onMenuClick={() => setTableOfContentVisible(!tableOfContentIsVisible)}
                />
                <Sections.FooterCTA />
                <Footer hideLangSelector={true} />
            </div>
        </>
    )
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
    const { query, resolvedUrl } = ctx
    const { locale, ...commonProps } = await getCommonServerSideProps(ctx)

    /** Only English is supported for now */
    const blogPost = await contentful.getBlogPost(query.slug as string, locale)

    if (!blogPost) {
        return {
            notFound: true,
        }
    }

    return {
        props: {
            blogPost,
            postUrl: commonProps.absoluteUrl,
            resolvedUrl,
            ...commonProps,
            ...(await serverSideTranslations(locale, ['common'])),
        },
    }
}
